import { useMemo } from 'react';
import { Card, Text, Button, Stack, Cluster } from '../ui';
import { stageCounts, type SaleroomChargeRow } from '../../lib/saleroom-admin-queries';
import type { AdminView } from '../../routes/Admin';

/**
 * Admin home: "what needs you" at a glance. Each card deep-links into the view
 * that clears it. Keeps the team oriented without hunting through navigation.
 */

export function AdminDashboard({
  rows, unread, displayName, onGo,
}: {
  rows: SaleroomChargeRow[];
  unread: number;
  displayName: string | null;
  onGo: (view: AdminView) => void;
}) {
  const counts = useMemo(() => stageCounts(rows), [rows]);

  const cards: Array<{ n: number; label: string; hint: string; view: AdminView; accent?: boolean }> = [
    { n: counts.ready_to_ship, label: 'Ready to ship', hint: 'Buy the label and post', view: 'orders', accent: true },
    { n: counts.needs_action + counts.expired, label: 'Needs action', hint: 'Declines, SCA, no card, or forfeited', view: 'orders' },
    { n: counts.shipped,       label: 'In transit',    hint: 'On the way to buyers', view: 'orders' },
    { n: unread,               label: 'Messages',      hint: 'Unread in the inbox', view: 'inbox' },
  ];

  const nothing = cards.every((c) => c.n === 0);

  return (
    <Stack gap="loose">
      <div>
        <p className="kicker">Dashboard</p>
        <Text role="lede" muted>
          {nothing ? "You're all caught up - nothing needs you right now." : 'Here is what needs you.'}
        </Text>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((c) => (
          <Card key={c.label} bordered pad="md" className={c.accent && c.n > 0 ? 'border-accent' : undefined}>
            <button type="button" onClick={() => onGo(c.view)} className="flex w-full flex-col items-start gap-1 text-left">
              <span className="text-3xl font-semibold leading-none tabular-nums text-text">{c.n}</span>
              <Text role="label" as="span">{c.label}</Text>
              <Text role="caption" faint>{c.hint}</Text>
            </button>
          </Card>
        ))}
      </div>

      <div>
        <Text role="eyebrow">Jump to</Text>
        <Cluster gap="tight" wrap className="mt-2">
          <Button type="button" variant="soft" size="sm" onClick={() => onGo('orders')}>Orders</Button>
          <Button type="button" variant="soft" size="sm" onClick={() => onGo('posts')}>Posts</Button>
          <Button type="button" variant="soft" size="sm" onClick={() => onGo('members')}>Members</Button>
          <Button type="button" variant="soft" size="sm" onClick={() => onGo('auctions')}>Live auctions</Button>
          <Button type="button" variant="soft" size="sm" onClick={() => onGo('campaigns')}>Email campaign</Button>
        </Cluster>
      </div>

      <Text role="caption" faint>
        {displayName ? `Signed in as ${displayName}.` : null}
      </Text>
    </Stack>
  );
}
